<!--
/**
 * Daily Digest Quotes — shared for WhatsApp + Telegram digests
 */
 -->

<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-card-container">
      <oxd-text tag="h6" class="orangehrm-main-title">
        Daily Digest Quotes
      </oxd-text>
      <oxd-divider />

      <div class="orangehrm-information-card-container">
        <oxd-text class="orangehrm-sub-title">Note:</oxd-text>
        <ul>
          <li>
            <oxd-text class="orangehrm-information-card-text">
              Upload 31 quotes (one per day of the month). Day 1 = quote row 1.
            </oxd-text>
          </li>
          <li>
            <oxd-text class="orangehrm-information-card-text">
              Quotes appear in both WhatsApp and Telegram daily digests.
            </oxd-text>
          </li>
          <li>
            <oxd-text class="orangehrm-information-card-text">
              Re-uploading the same month replaces existing quotes.
            </oxd-text>
          </li>
          <li>
            <oxd-text class="orangehrm-information-card-text">
              Sample Excel:
              <a
                href="#"
                class="download-link"
                @click.prevent="onDownloadSample"
              >
                Download
              </a>
            </oxd-text>
          </li>
        </ul>
      </div>
      <br />

      <oxd-text v-if="statusBanner" tag="p" class="orangehrm-input-hint">
        {{ statusBanner }}
      </oxd-text>

      <oxd-form :loading="isLoading" @submitValid="onUpload">
        <oxd-form-row>
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="form.yearMonth"
                label="Month (YYYY-MM)"
                placeholder="2026-09"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="form.attachment"
                type="file"
                label="Quote Excel (.xlsx)"
                :button-label="'Browse'"
                :placeholder="'No file selected'"
                :rules="rules.attachment"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
          <oxd-form-actions>
            <submit-button label="Upload" />
          </oxd-form-actions>
        </oxd-form>

        <oxd-form-row style="margin-top: 0.5rem">
          <oxd-button
            display-type="secondary"
            label="Refresh list"
            :disabled="isLoading"
            @click="loadStatus"
          />
        </oxd-form-row>

      <oxd-divider />
      <oxd-text tag="h6" class="orangehrm-main-title">
        Quotes for {{ status.yearMonth || form.yearMonth }}
      </oxd-text>
      <oxd-text
        v-if="!status.quotes || !status.quotes.length"
        tag="p"
        class="orangehrm-input-hint"
      >
        No quotes uploaded for this month yet.
      </oxd-text>
      <table
        v-else
        class="orangehrm-container"
        style="width: 100%; border-collapse: collapse; margin-top: 0.5rem"
      >
        <thead>
          <tr>
            <th style="text-align: left; padding: 6px; border-bottom: 1px solid #ddd; width: 80px">
              Day
            </th>
            <th style="text-align: left; padding: 6px; border-bottom: 1px solid #ddd">
              Quote
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in status.quotes" :key="row.dayNumber">
            <td style="padding: 6px; border-bottom: 1px solid #eee; vertical-align: top">
              {{ row.dayNumber }}
            </td>
            <td style="padding: 6px; border-bottom: 1px solid #eee">
              {{ row.quoteText }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import {required, maxFileSize, validFileTypes} from '@/core/util/validation/rules';
import {APIService} from '@/core/util/services/api.service';

const defaultMonth = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
};

export default {
  name: 'DailyDigestQuotes',
  setup() {
    const http = new APIService(
      window.appGlobal.baseUrl,
      'api/v2/digest-quotes',
    );
    return {http};
  },
  data() {
    return {
      isLoading: false,
      form: {
        yearMonth: defaultMonth(),
        attachment: null,
      },
      status: {
        yearMonth: '',
        uploaded: false,
        quotes: [],
        nextYearMonth: '',
        nextMonthUploaded: false,
        reminderActive: false,
        message: '',
      },
      rules: {
        attachment: [
          required,
          validFileTypes(['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet']),
          maxFileSize(1024 * 1024 * 2),
        ],
      },
    };
  },
  computed: {
    statusBanner() {
      const parts = [];
      if (this.status.uploaded) {
        parts.push(
          `Loaded ${this.status.quoteCount || 0} quotes` +
            (this.status.uploadedAt ? ` (uploaded ${this.status.uploadedAt})` : ''),
        );
      }
      if (this.status.reminderActive) {
        parts.push(
          `Reminder active: please upload ${this.status.nextYearMonth} before month end.`,
        );
      } else if (this.status.nextMonthUploaded) {
        parts.push(`Next month (${this.status.nextYearMonth}) already uploaded.`);
      }
      if (this.status.message) {
        parts.push(this.status.message);
      }
      return parts.join(' | ');
    },
  },
  created() {
    this.loadStatus();
  },
  methods: {
    onDownloadSample() {
      window.location.href = `${window.appGlobal.baseUrl}/whatsapp/digestQuotesSampleDownload`;
    },
    loadStatus() {
      this.isLoading = true;
      const ym = this.form.yearMonth || defaultMonth();
      this.http
        .request({
          method: 'GET',
          params: {yearMonth: ym},
        })
        .then(({data}) => {
          this.status = Object.assign({}, this.status, data.data || {});
          if (this.status.yearMonth) {
            this.form.yearMonth = this.status.yearMonth;
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    onUpload() {
      this.isLoading = true;
      this.http
        .request({
          method: 'POST',
          data: {
            yearMonth: this.form.yearMonth,
            attachment: this.form.attachment,
          },
        })
        .then(({data}) => {
          this.status = Object.assign({}, this.status, data.data || {});
          this.form.attachment = null;
          this.$toast.success({
            title: 'Success',
            message: this.status.message || 'Quotes uploaded',
          });
        })
        .catch(() => {
          this.$toast.error({
            title: 'Error',
            message: 'Upload failed. Check the Excel format (31 days required).',
          });
        })
        .finally(() => {
          this.isLoading = false;
          this.loadStatus();
        });
    },
  },
};
</script>

<style scoped>
.download-link {
  color: #ff7b1d;
  text-decoration: underline;
  cursor: pointer;
}
.orangehrm-information-card-container ul {
  margin: 0.5rem 0 0;
  padding-left: 1.25rem;
}
</style>
